import { Component } from '@theme/component';


/**
 * Product Custom Property
 *
 * Handles:
 *
 * 1. Custom text
 * 2. Character counter
 * 3. Image upload
 * 4. Image preview
 * 5. Image removal
 *
 * IMPORTANT:
 *
 * Horizon's actual product form is:
 *
 * BuyButtons-ProductForm-{{ section.id }}
 *
 * The Liquid block associates the inputs with
 * that form using the HTML "form" attribute.
 *
 * Horizon's own product-form.js then uses:
 *
 * new FormData(form)
 *
 * so both the text property and image file
 * are included when the product is added.
 */
class ProductCustomProperty extends Component {


  /*
   * ============================================================
   * CONNECTED
   * ============================================================
   */

  connectedCallback() {

    super.connectedCallback?.();


    this.#setupForm();

    this.#setupText();

    this.#setupImage();

  }



  /*
   * ============================================================
   * FIND HORIZON PRODUCT FORM
   * ============================================================
   */

  #getProductForm() {

    const formId =
      this.dataset.productFormId;


    /*
     * First:
     * Use the exact Horizon product form ID.
     */

    if (formId) {

      const form =
        document.getElementById(formId);


      if (form) {

        return form;

      }

    }


    /*
     * Fallback:
     * Horizon product form.
     */

    const horizonForm =
      document.querySelector(
        'form[data-type="add-to-cart-form"]'
      );


    if (horizonForm) {

      return horizonForm;

    }


    /*
     * Final fallback.
     */

    return document.querySelector(
      'form[action*="/cart/add"]'
    );

  }



  /*
   * ============================================================
   * SETUP FORM
   * ============================================================
   */

  #setupForm() {

    const form =
      this.#getProductForm();


    if (!form) {

      console.warn(
        '[Product Custom Property] Horizon product form not found.'
      );

      return;

    }


    /*
     * File uploads require multipart/form-data
     * for normal browser form submissions.
     *
     * Horizon currently creates FormData(form)
     * when using AJAX Add to Cart, but keeping
     * this attribute is still correct.
     */

    form.setAttribute(
      'enctype',
      'multipart/form-data'
    );


    form.setAttribute(
      'encoding',
      'multipart/form-data'
    );


    /*
     * Make sure our fields are associated
     * with the correct Horizon form.
     */

    const {
      textInput,
      imageInput
    } = this.refs;


    if (textInput) {

      textInput.setAttribute(
        'form',
        form.id
      );

    }


    if (imageInput) {

      imageInput.setAttribute(
        'form',
        form.id
      );

    }

  }



  /*
   * ============================================================
   * TEXT INPUT
   * ============================================================
   */

  #setupText() {

    const {
      textInput
    } = this.refs;


    if (!textInput) {

      return;

    }


    /*
     * Initial character count.
     */

    this.#updateCharacterCount();


    /*
     * Update while typing.
     */

    textInput.addEventListener(
      'input',
      () => {

        this.#updateCharacterCount();

      }
    );

  }



  /*
   * ============================================================
   * CHARACTER COUNT
   * ============================================================
   */

  #updateCharacterCount() {

    const {
      characterCount,
      textInput
    } = this.refs;


    if (
      !characterCount ||
      !textInput
    ) {

      return;

    }


    const currentLength =
      textInput.value.length;


    const maxLength =
      textInput.maxLength;


    const template =
      characterCount.getAttribute(
        'data-template'
      );


    if (!template) {

      return;

    }


    characterCount.textContent =
      template
        .replace(
          '[current]',
          currentLength.toString()
        )
        .replace(
          '[max]',
          maxLength.toString()
        );

  }



  /*
   * ============================================================
   * IMAGE INPUT
   * ============================================================
   */

  #setupImage() {

    const {
      imageInput,
      removeImage
    } = this.refs;


    if (!imageInput) {

      return;

    }


    /*
     * Image selected.
     */

    imageInput.addEventListener(
      'change',
      () => {

        this.#handleImageChange();

      }
    );


    /*
     * Remove image.
     */

    if (removeImage) {

      removeImage.addEventListener(
        'click',
        () => {

          this.#removeImage();

        }
      );

    }

  }



  /*
   * ============================================================
   * IMAGE CHANGE
   * ============================================================
   */

  #handleImageChange() {

    const {
      imageInput
    } = this.refs;


    if (!imageInput) {

      return;

    }


    const file =
      imageInput.files?.[0];


    /*
     * No file.
     */

    if (!file) {

      this.#clearImage();

      return;

    }


    /*
     * Check file type.
     */

    if (
      !file.type.startsWith(
        'image/'
      )
    ) {

      alert(
        'Please select a valid image.'
      );


      imageInput.value = '';


      this.#clearImage();


      return;

    }


    /*
     * Optional size protection.
     *
     * 10 MB maximum.
     */

    const maxFileSize =
      10 * 1024 * 1024;


    if (
      file.size > maxFileSize
    ) {

      alert(
        'Please select an image smaller than 10 MB.'
      );


      imageInput.value = '';


      this.#clearImage();


      return;

    }


    /*
     * Show filename.
     */

    const {
      fileName
    } = this.refs;


    if (fileName) {

      fileName.textContent =
        file.name;


      fileName.hidden =
        false;

    }


    /*
     * Preview.
     */

    const {
      imagePreview,
      previewImage
    } = this.refs;


    if (
      !imagePreview ||
      !previewImage
    ) {

      return;

    }


    const reader =
      new FileReader();


    reader.onload =
      (event) => {

        const result =
          event.target?.result;


        if (!result) {

          return;

        }


        previewImage.src =
          result;


        imagePreview.hidden =
          false;

      };


    reader.onerror =
      () => {

        console.warn(
          '[Product Custom Property] Could not preview image.'
        );

      };


    reader.readAsDataURL(
      file
    );

  }



  /*
   * ============================================================
   * REMOVE IMAGE
   * ============================================================
   */

  #removeImage() {

    const {
      imageInput
    } = this.refs;


    if (imageInput) {

      imageInput.value = '';

    }


    this.#clearImage();

  }



  /*
   * ============================================================
   * CLEAR IMAGE
   * ============================================================
   */

  #clearImage() {

    const {
      imagePreview,
      previewImage,
      fileName
    } = this.refs;


    /*
     * Remove preview.
     */

    if (previewImage) {

      previewImage.removeAttribute(
        'src'
      );

    }


    /*
     * Hide preview.
     */

    if (imagePreview) {

      imagePreview.hidden =
        true;

    }


    /*
     * Remove filename.
     */

    if (fileName) {

      fileName.textContent =
        '';

      fileName.hidden =
        true;

    }

  }

}



/*
 * ============================================================
 * REGISTER COMPONENT
 * ============================================================
 */

if (
  !customElements.get(
    'product-custom-property-component'
  )
) {

  customElements.define(
    'product-custom-property-component',
    ProductCustomProperty
  );

}